import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, Tag, Progress } from 'antd';
import style from './prevention.scss';
import ThresholdValueCard from '../components/thresholdValueCard/thresholdValueCard';
import { InfoCircleOutlined } from '@ant-design/icons';
import ConfigureDrawer from '../../trm/configureInstanceDashboard/ConfigureDrawer';
import * as api from '../../../services/trm/trmServices';
import TablePagination from '../../cm/common/TablePagination';
import { toLower } from '../../../util/lodashlike';
import { buildVariantDrawerRecord } from './variantDrawerRecord';
import { groupingKeyDisplayName } from './groupingKeyLabel';
import { computeGroupRowSpan } from './rowSpan';
import { stripTypologyPrefix } from '../../../util/typology';
import useAlertLevelConfigs from '../../common/hooks/useAlertLevelConfig';
const networkLayerObject = new api.NetworkLayer();

function PreventionTable({
  scoreExplainabilityData,
  ruleContributions,
  setRolesState,
  alertInfo,
  trmActions,
  isLoading,
  // FIN-11960 — query-builder field metadata (key -> { label }) used to render
  // Group By with the same human-readable names as the Rule config screen.
  fieldsMetaData,
  // FIN-12133 — alert_level_details configs (code -> { displayName, color }); the
  // Action value is colour-coded by its configured severity's level colour.
  alertLevelConfigs = []
}) {
  const drawerRef = useRef(null);
  const { getLevelColor } = useAlertLevelConfigs(alertLevelConfigs);

  const [renderData, setRenderData] = useState([]);
  // The dataset in the order the Table actually displays it (after sort /
  // filter), captured from Table onChange extra.currentDataSource. The
  // typology/variant rowSpan merges must compare neighbours in DISPLAY order —
  // comparing against renderData's original order breaks every cell merge the
  // moment the user sorts by Score (rows shift one column left).
  const [displayData, setDisplayData] = useState(null);
  const [dropdownRoleValue, setDropdownRoleValue] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 10
  });

  useEffect(() => {
    let initialRoles = [];
    if (Array.isArray(renderData)) {
      renderData.map((data) => {
        initialRoles = [...initialRoles, ...data.entityRole];
      });
      setDropdownRoleValue(initialRoles);
    }
  }, [renderData]);

  useEffect(() => {
    setRolesState(dropdownRoleValue);
  }, [dropdownRoleValue]);

  const onOpenDrawer = useCallback(
    (triggerName) => {
      if (triggerName) {
        networkLayerObject
          .trmScenarioTypologiesListAPI({
            scenarioID: alertInfo.scenarioId
          })
          .then((res) => {
            // FIN-12017 — build the record via a null-safe helper. Previously
            // a typologyId absent from the scenario's typologies (cloned
            // scenario / variant-id drift) or a null typology.rules made
            // JSON.parse(typologyInfo.rules) throw inside this .then(), so
            // showDrawer never ran and the drawer spinner hung forever. The
            // helper never throws and always yields a usable record.
            drawerRef.current?.showDrawer(
              0,
              buildVariantDrawerRecord(
                alertInfo.triggeredRules,
                res?.typologies,
                triggerName
              )
            );
          })
          .catch(() => {
            // Never leave the spinner hanging on an API/parse failure — open
            // the drawer with the variant name so the analyst sees the
            // available info instead of an infinite spinner. (FIN-12017)
            drawerRef.current?.showDrawer(0, {
              variantName: triggerName,
              typologiesReadOnly: []
            });
          });
      }
      // alertInfo (the alert-detail payload carrying scenarioId + triggeredRules)
      // resolves asynchronously after this component first mounts off the
      // score-explainability fetch. With an empty dep array the callback captured
      // the initial (null) alertInfo, so the InfoCircle click threw
      // "Cannot read properties of null (reading 'scenarioId')" and the Typology
      // Drawer never opened. Depend on alertInfo so the handler always reads the
      // resolved payload. (FIN-11542)
    },
    [alertInfo]
  );

  // FIN-12133: the configured per-action severity (typology_actions.action_severity:
  // Low/Good/High) from the Action Catalog, matched case-insensitively on the action
  // display name. This is the per-rule severity, distinct from the per-alert level
  // computed from alert_score (shown as the alert-level badge).
  const actionSeverity = (action) =>
    trmActions?.data?.find(
      ({ displayName }) => toLower(displayName) === toLower(action)
    )?.actionSeverity;

  const columns = [
    {
      title: 'Typology Name',
      dataIndex: 'typologyName',
      key: 'typologyName',
      width: '15%',
      render: (typologyName, record, index) => {
        const obj = {
          children: (
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
              {stripTypologyPrefix(typologyName)}
            </span>
          ),
          props: {}
        };
        const orderedData = displayData || renderData;
        const trueIndex =
          index + paginationInfo.pageSize * (paginationInfo.current - 1);
        // FIN-12179 — merge consecutive rows of the SAME typology only.
        obj.props.rowSpan = computeGroupRowSpan(
          orderedData,
          index,
          trueIndex,
          (row) => row.typologyName
        );
        return obj;
      }
    },
    {
      title: 'Variant Name',
      dataIndex: 'triggerName',
      key: 'triggerName',
      width: '15%',
      render: (triggerName) => {
        // FIN-12179 — Variant Name is NOT row-merged. A Configure's variant-clones
        // reuse the same rule (triggerName), so merging by triggerName blanked the
        // 2nd variant's name and, when a name repeated across typologies, over-ran
        // the span and shifted later columns under the wrong headers. Each variant
        // row renders its own name (default rowSpan 1).
        const obj = {
          children: (
            <span
              style={{
                display: 'flex',
                gap: '10px'
              }}>
              {triggerName}
              <InfoCircleOutlined
                onClick={() => onOpenDrawer(triggerName)}
                style={{ fontSize: '16px', color: '#08c', cursor: 'pointer' }}
              />
            </span>
          ),
          props: {}
        };
        return obj;
      }
    },
    {
      title: 'Group By',
      dataIndex: 'entityRole',
      key: 'entityRole',
      width: '8%',
      render: (entityRole) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {entityRole
            ? entityRole.map((ent) => (
                <Tag
                  style={{
                    borderRadius: '25px',
                    width: 'fit-content',
                    // FIN-13962: wrap long keys so Group By can stay narrow.
                    whiteSpace: 'normal'
                  }}
                  color="red">
                  {groupingKeyDisplayName(ent, fieldsMetaData)}
                </Tag>
              ))
            : ''}
        </div>
      )
    },
    {
      title: 'Function Name | Value',
      dataIndex: 'functionValue',
      key: 'functionValue',
      width: '32%',
      render: (functionValueArray) => {
        const obj = {
          children: <ThresholdValueCard value={functionValueArray} />,
          props: {}
        };
        return obj;
      }
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '10%',
      render: (text, record) => (
        <div className={style.actionCard}>
          <p
            style={{
              textTransform: 'capitalize',
              // FIN-11255: colour-code the Action by the rule's as-scored severity
              // (record.severity, from the snapshot), via its level colour in
              // alert_level_details. Same source as the Score bar below.
              color: getLevelColor(record.severity)
            }}>
            {text}
          </p>
        </div>
      )
    },
    {
      title: 'Score',
      dataIndex: 'effectiveScore',
      key: 'effectiveScore',
      width: '10%',
      sorter: (a, b) => (a.effectiveScore || 0) - (b.effectiveScore || 0),
      // FIN-12233 — no defaultSortOrder: a default score-sort reorders rows on
      // mount WITHOUT firing onChange, so the typology rowSpan (computed against
      // renderData via computeGroupRowSpan) merges the wrong rows and repeats the
      // typology name. Grouping is the primary layout; sorting stays on-demand
      // (onChange then captures displayData and rowSpan recomputes correctly).
      // Score visual moved here from ScoreComputationCard — the card now only
      // carries the aggregation summary, so the per-rule bar lives with the
      // per-rule detail instead of duplicating it.
      render: (score, record) =>
        score != null ? (
          <div className={style.scoreCell}>
            <Progress
              percent={Math.min(parseFloat(score), 100)}
              showInfo={false}
              strokeColor={getLevelColor(record.severity) || '#848484'}
              size="small"
              className={style.scoreCellBar}
            />
            <span>{parseFloat(score).toFixed(2)}</span>
          </div>
        ) : (
          '—'
        )
    },
    {
      // Restored (FIN-11979): the Additional Info column was dropped in
      // 99d5abbc12 when Score was added. The BE still returns
      // trigger.additionalInfo, so re-map it (see onRenderData) and show it as
      // the last column, after Score.
      title: 'Additional Info',
      dataIndex: 'additionalInfo',
      key: 'additionalInfo',
      width: '10%',
      render: (text) => <div>{text || '—'}</div>
    }
  ];

  useEffect(() => {
    if (scoreExplainabilityData) {
      onRenderData(scoreExplainabilityData);
    }
  }, [scoreExplainabilityData, ruleContributions]);

  const onRenderData = (dataSource) => {
    const flattenedData = Object.keys(dataSource)
      .reduce((acc, typologyKey) => {
        const typologyName = typologyKey;
        const triggers = dataSource[typologyKey];

        const infoDetails = triggers?.map((trigger, index) => {
          const functionValueArray = Object.entries(trigger.functionValue).map(
            ([key, value]) => ({
              key,
              value: value.value
            })
          );

          const contribution = ruleContributions?.find(
            (r) => r.ruleName === trigger.triggerName
          );

          return {
            key: `${typologyName}-${index}`,
            typologyName: typologyName,
            triggerName: trigger.triggerName,
            functionValue: functionValueArray,
            action: trigger.action,
            additionalInfo: trigger.additionalInfo,
            rowSpan: index === 0 ? triggers.length : 0,
            entityRole: trigger.entityRole,
            effectiveScore: contribution?.effectiveScore,
            // FIN-11255: per-rule severity as-scored (snapshot), from the API
            // (triggered_rules); drives the Action + score-bar colour via the level
            // colour in alert_level_details. Falls back to the live Action Catalog
            // only for legacy alerts scored before ruleSeverity existed (client-side,
            // no API cost).
            severity: trigger.ruleSeverity ?? actionSeverity(trigger.action)
          };
        });

        return [...acc, ...infoDetails];
      }, [])
      .filter(
        (v, i, a) =>
          a.findIndex((v2) =>
            ['triggerName', 'functionValue'].every(
              (k) => v2[k]?.toString() === v[k]?.toString()
            )
          ) === i
      );

    setRenderData(flattenedData);
    setDisplayData(null);
  };

  const handleChange = (pagination, pageSize) => {
    setPaginationInfo({
      current: pagination,
      pageSize: pageSize
    });
  };

  return (
    <>
      <Table
        // Capture the sorted/filtered order the Table is actually rendering so
        // the rowSpan merges above compare display-order neighbours.
        onChange={(pagination, filters, sorter, extra) =>
          setDisplayData(extra?.currentDataSource || null)
        }
        className={style.ScoreTable}
        columns={columns}
        dataSource={renderData}
        bordered
        loading={isLoading}
        pagination={{
          total: renderData?.length || 0,
          ...paginationInfo,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: handleChange,
          showTotal: (total) => (
            <TablePagination
              selectedItemsCount={null}
              total={total}
              currentPage={paginationInfo.current}
              pageSize={paginationInfo.pageSize}
            />
          )
        }}
      />
      <ConfigureDrawer typologies={[]} ref={drawerRef} isReadOnly={true} />
    </>
  );
}

export default PreventionTable;
